import { ReactNode } from 'react';

interface TreeItemWrapperProps {
  children: ReactNode;
  depth: number;
  isLastChild?: boolean;
}

/**
 * Shared layout constants — must match TreeItem.tsx exactly.
 *
 * TreeItem paddingLeft = depth * (INDENT_SIZE + BASE_PADDING) = depth * 28
 *
 * For a child at depth D:
 *   - Parent is at depth D-1, paddingLeft = (D-1) * 28
 *   - Parent chevron (16px wide) center = (D-1) * 28 + 8
 *   - Guide line X  = (D-1) * 28 + 8
 *   - Child content starts at D * 28
 *   - Connector width = D*28 - ((D-1)*28 + 8) = 20  (constant for all depths)
 */
const INDENT_SIZE = 16;
const BASE_PADDING = 12;
const TOTAL_INDENT = INDENT_SIZE + BASE_PADDING; // 28px per depth level
const CHEVRON_CENTER = INDENT_SIZE / 2; // 8px — center of parent's chevron icon
const CONNECTOR_WIDTH = TOTAL_INDENT - CHEVRON_CENTER; // 20px constant

/**
 * ~Half the row height. TreeItem: py-1.5 (6+6px) + text-sm line-height (20px) ≈ 32px total.
 * The horizontal connector taps into the vertical mid-point of the first row.
 */
const ROW_MID = 16;

/**
 * Stacking context explanation:
 *
 * Folder TreeItems are `sticky` with a positive z-index, which creates a stacking
 * context. CSS paints positive-z-index elements AFTER z-index:auto elements, so a
 * folder button's `bg-background` covers guide lines that have no z-index — making
 * the lines invisible behind nested folders (but visible behind files, which have no
 * bg-background and no z-index).
 *
 * Fix: give guide lines a z-index that sits strictly between the child folder's
 * z-index and the parent folder's z-index. We use a low range (2–20) so the tree
 * stays below modals/popovers:
 *
 *   Parent folder  depth D-1 → z-index max(20 - 2*(D-1), 2)
 *   Guide lines    depth D   → z-index max(21 - 2*D, 2)     ← between
 *   Child folder  depth D   → z-index max(20 - 2*D, 2)
 *
 * This must stay in sync with the z-index formula in TreeItem.tsx.
 */

/**
 * Draws VS Code-style indent guide lines for each nested tree item:
 *  - A 1px vertical guide that runs from the top of this wrapper to either the
 *    row midpoint (last child) or the full bottom of the subtree (non-last child).
 *  - A 1px horizontal connector from the guide to the start of the item content.
 */
export function TreeItemWrapper({ children, depth, isLastChild }: TreeItemWrapperProps) {
  if (depth === 0) {
    return <>{children}</>;
  }

  const guideX = (depth - 1) * TOTAL_INDENT + CHEVRON_CENTER;
  // Must sit above child folder (20-2*depth) but below parent (22-2*depth). See comment above.
  const guideZ = Math.max(21 - 2 * depth, 2);

  return (
    <div className="relative">
      {/* Vertical indent guide */}
      <div
        className="pointer-events-none absolute w-px bg-muted-foreground/20"
        style={{
          left: `${guideX}px`,
          top: 0,
          height: isLastChild ? `${ROW_MID}px` : '100%',
          zIndex: guideZ,
        }}
      />

      {/* Horizontal connector to item */}
      <div
        className="pointer-events-none absolute h-px bg-muted-foreground/20"
        style={{
          left: `${guideX}px`,
          width: `${CONNECTOR_WIDTH}px`,
          top: `${ROW_MID}px`,
          zIndex: guideZ,
        }}
      />

      {children}
    </div>
  );
}
